import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  FormGroup,
  FormControl,
  ControlLabel,
  Tabs,
  Tab,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { updateRecipe, getRecipes } from "@beers/services/recipeService";
import { getBeerstyles } from "@beers/services/beerstyleService";
import { put } from "@common/services/request";
import { apiEndpoints as api } from "@common/constants/urls";
import Co2ConverterModal from "../CreateRecipe/Recipe/Co2ConverterModal";

const EditModal = ({
  show,
  closeEditModal,
  closeNotesModal,
  type,
  recipe,
  beer,
  refetchRecipes,
}) => {
  const handleClose = closeEditModal || closeNotesModal;
  const [name, setName] = useState("");
  const [styleId, setStyleId] = useState("");
  const [styles, setStyles] = useState([]);
  const [carbonation, setCarbonation] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  const [publicNote, setPublicNote] = useState("");
  const [activeTab, setActiveTab] = useState("private");
  const [activeRecipe, setActiveRecipe] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showCo2Modal, setShowCo2Modal] = useState(false);

  useEffect(() => {
    getBeerstyles()
      .then((data) => {
        if (Array.isArray(data)) {
          setStyles(data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (show) {
      setName(beer?.name || "");
      setStyleId(beer?.style?.id || "");
      const carb = recipe?.carbonation ?? beer?.carbonation ?? "";
      setCarbonation(
        carb !== null && carb !== undefined && carb !== "" ? String(carb) : "",
      );
      setActiveTab(type === "public_note" ? "public" : "private");
      setError(null);

      if (recipe) {
        setActiveRecipe(recipe);
        setPrivateNote(recipe?.private_note || "");
        setPublicNote(recipe?.public_note || "");
      } else if (beer && beer.id) {
        getRecipes(beer.id)
          .then((res) => {
            if (Array.isArray(res) && res.length > 0) {
              const latest = res[res.length - 1];
              setActiveRecipe(latest);
              setPrivateNote(latest.private_note || "");
              setPublicNote(latest.public_note || "");
              if (
                (carb === "" || carb === null || carb === undefined) &&
                latest.carbonation
              ) {
                setCarbonation(String(latest.carbonation));
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [show, recipe, beer, type]);

  const isBrewed = Boolean(
    (recipe && +recipe.times_brewed > 0) ||
      (activeRecipe && +activeRecipe.times_brewed > 0) ||
      beer?.latest_brew_date,
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Beer name is required");
      return;
    }

    setIsSaving(true);
    setError(null);

    const carbFormatted =
      carbonation !== "" && !isNaN(parseFloat(carbonation))
        ? parseFloat(carbonation).toFixed(2)
        : null;

    try {
      const updates = [];

      if (beer && beer.id) {
        const beerPayload = {
          id: beer.id,
          name: name.trim(),
          style: styleId || beer.style?.id,
          carbonation: carbFormatted,
        };
        updates.push(put(`${api.beers}${beer.id}/`, beerPayload));
      }

      const targetRecipe = recipe || activeRecipe;
      if (targetRecipe && targetRecipe.id) {
        const recipePayload = {
          ...targetRecipe,
          private_note: privateNote,
          public_note: publicNote,
          carbonation: carbFormatted,
        };

        if (isBrewed) {
          const notesChanged =
            (targetRecipe.private_note || "") !== privateNote ||
            (targetRecipe.public_note || "") !== publicNote;
          if (notesChanged) {
            updates.push(updateRecipe(recipePayload));
          }
        } else {
          updates.push(updateRecipe(recipePayload));
        }
      }

      await Promise.all(updates);

      if (refetchRecipes) {
        await refetchRecipes();
      }
      if (handleClose) handleClose();
    } catch (err) {
      const respData = err?.response?.data;
      let errorMsg = "Failed to save changes. Please try again.";
      if (Array.isArray(respData) && respData.length > 0) {
        errorMsg = respData[0];
      } else if (respData && typeof respData === "object") {
        const firstKey = Object.keys(respData)[0];
        const val = respData[firstKey];
        errorMsg = Array.isArray(val) ? `${firstKey}: ${val[0]}` : String(val);
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  if (!recipe && !beer) return null;

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontWeight: "bold", textAlign: "center" }}>
          EDIT BEER DETAILS & NOTES
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isBrewed && (
          <Alert
            bsStyle="info"
            style={{
              fontSize: "13px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <span
              className="ion-information-circled"
              style={{ fontSize: "18px", flexShrink: 0, marginTop: "-2px" }}
            />
            <div>
              <strong>Recipe has been brewed:</strong> This recipe cannot be
              edited directly because it has already been brewed. If you need to
              edit ingredients, please create a new version instead. You can
              still edit the beer details, carbonation, and notes below.
            </div>
          </Alert>
        )}

        {error && <Alert bsStyle="danger">{error}</Alert>}

        <Row>
          <Col xs={12} sm={6}>
            <FormGroup controlId="editBeerName">
              <ControlLabel>Beer Name *</ControlLabel>
              <FormControl
                type="text"
                placeholder="e.g. American IPA"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col xs={12} sm={6}>
            <FormGroup controlId="editBeerStyle">
              <ControlLabel>Style</ControlLabel>
              <FormControl
                componentClass="select"
                value={styleId}
                onChange={(e) => setStyleId(e.target.value)}
              >
                <option value="">Select style...</option>
                {styles.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <FormGroup controlId="editBeerCarbonation">
              <ControlLabel>Carbonation (g/L CO2)</ControlLabel>
              <FormControl
                type="number"
                step="0.01"
                min="0"
                max="15"
                placeholder="e.g. 5.00"
                value={carbonation}
                onChange={(e) => setCarbonation(e.target.value)}
              />
              <div style={{ marginTop: "4px" }}>
                <a
                  href="#convert-co2"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowCo2Modal(true);
                  }}
                  style={{
                    fontSize: "12px",
                    color: "#337ab7",
                    cursor: "pointer",
                    textDecoration: "underline",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span
                    className="ion-calculator"
                    style={{ fontSize: "13px" }}
                  />
                  <span>Convert from vol. CO2</span>
                </a>
              </div>
            </FormGroup>
          </Col>
        </Row>

        <div style={{ marginTop: "15px" }}>
          <Tabs
            id="modal-notes-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Tab eventKey="private" title="PRIVATE NOTES">
              <div style={{ paddingTop: "12px" }}>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#777",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Visible only to you
                </span>
                <FormControl
                  style={{ minHeight: 120 }}
                  componentClass="textarea"
                  value={privateNote}
                  placeholder="Add private brewing/tasting notes..."
                  onChange={(e) => setPrivateNote(e.target.value)}
                />
              </div>
            </Tab>
            <Tab eventKey="public" title="PUBLIC NOTES">
              <div style={{ paddingTop: "12px" }}>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#777",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Visible to other users if you decide to share this recipe
                </span>
                <FormControl
                  style={{ minHeight: 120 }}
                  componentClass="textarea"
                  value={publicNote}
                  placeholder="Add public recipe notes..."
                  onChange={(e) => setPublicNote(e.target.value)}
                />
              </div>
            </Tab>
          </Tabs>
        </div>

        <Co2ConverterModal
          show={showCo2Modal}
          onHide={() => setShowCo2Modal(false)}
          onApply={(converted) => {
            setCarbonation(converted);
            setShowCo2Modal(false);
          }}
          initialValue={carbonation}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          bsStyle="primary"
          disabled={isSaving || !name.trim()}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EditModal.propTypes = {
  show: PropTypes.bool,
  closeEditModal: PropTypes.func,
  closeNotesModal: PropTypes.func,
  type: PropTypes.string,
  recipe: PropTypes.object,
  beer: PropTypes.object,
  refetchRecipes: PropTypes.func,
};

export default EditModal;
